import React, { useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination, TextField, MenuItem,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";

const STATUS_STYLE = {
    SUCCESS: { bgcolor: "#f0fdf4", color: "#16a34a", icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
    FAILED: { bgcolor: "#fef2f2", color: "#DC2626", icon: <ErrorIcon sx={{ fontSize: 12 }} /> },
};

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–";

const InternalJobsTab = ({
    internalJobsApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onRun,
    tabIndex = 5,
}) => {
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [lastStatusFilter, setLastStatusFilter] = useState("all"); // all|SUCCESS|FAILED|none
    const [sortBy, setSortBy] = useState("next_run_asc");

    const sourceData = Array.isArray(internalJobsApi.data) ? internalJobsApi.data : [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = sourceData.filter((j) => {
            if (activeFilter === "active" && !j.active) return false;
            if (activeFilter === "inactive" && j.active) return false;
            const st = j.last_status || "none";
            if (lastStatusFilter !== "all" && String(st) !== lastStatusFilter) return false;
            if (!q) return true;
            return String(j.job_key || "").toLowerCase().includes(q)
                || String(j.transaction_type || "").toLowerCase().includes(q);
        });
        rows.sort((a, b) => {
            if (sortBy === "job_key_asc") return String(a.job_key || "").localeCompare(String(b.job_key || ""), "fr");
            if (sortBy === "last_run_desc") return new Date(b.last_run_at || 0) - new Date(a.last_run_at || 0);
            return new Date(a.next_run_at || Number.MAX_SAFE_INTEGER) - new Date(b.next_run_at || Number.MAX_SAFE_INTEGER);
        });
        return rows;
    }, [sourceData, search, activeFilter, lastStatusFilter, sortBy]);

    if (internalJobsApi.error) return <ErrorState error={internalJobsApi.error} onRetry={() => internalJobsApi.refetch()} />;
    if (internalJobsApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (sourceData.length === 0) {
        return <EmptyState message="Aucun job interne configuré" color={SECTION_COLORS.internalJobs.main} />;
    }

    const pageRaw = pages[tabIndex] ?? 0;
    const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
    const page = Math.min(pageRaw, maxPage);
    const paginated = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher un job (job_key, event type)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "next_run_asc", label: "Prochain run (bientôt)" },
                    { value: "last_run_desc", label: "Dernier run (récent)" },
                    { value: "job_key_asc", label: "Job key (A-Z)" },
                ]}
            >
                <TextField
                    select
                    size="small"
                    label="Dernier statut"
                    value={lastStatusFilter}
                    onChange={(e) => setLastStatusFilter(e.target.value)}
                    sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="SUCCESS">Succès</MenuItem>
                    <MenuItem value="FAILED">Échec</MenuItem>
                    <MenuItem value="none">Aucun</MenuItem>
                </TextField>
            </ListToolbar>
            <Grid container spacing={2}>
                {paginated.map((job) => {
                    const lastStatus = job.last_status;
                    const statusStyle = STATUS_STYLE[lastStatus] || null;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={job.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.internalJobs.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.internalJobs.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.internalJobs.main}30`,
                                },
                            }}>
                                {/* transaction_type badge */}
                                <Tooltip title={`Event type : ${job.transaction_type}`} placement="left">
                                    <Chip label={job.transaction_type} size="small"
                                        sx={{
                                            position: "absolute", top: 10, right: 10,
                                            fontWeight: 700, fontSize: 10,
                                            bgcolor: "#f0f9ff", color: "#0369a1", maxWidth: 120,
                                        }} />
                                </Tooltip>

                                {/* Header */}
                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={14}>
                                    <WorkIcon sx={{ color: SECTION_COLORS.internalJobs.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {job.job_key}
                                    </Typography>
                                </Box>

                                {/* Brand */}
                                {job.brand && (
                                    <Chip label={job.brand} size="small"
                                        sx={{ mb: 1, fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#15803d" }} />
                                )}

                                {/* Schedule */}
                                {job.schedule?.cron && (
                                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                                        <ScheduleIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                                        <Typography fontSize={11} color="text.secondary" fontFamily="monospace">
                                            {job.schedule.cron}
                                            {job.schedule.timezone && job.schedule.timezone !== "UTC" && ` (${job.schedule.timezone})`}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Next run */}
                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {job.next_run_at && (
                                        <Chip label={`Prochain : ${fmtDate(job.next_run_at)}`} size="small"
                                            sx={{ fontSize: 10, bgcolor: "#f0f9ff", color: "#0369a1" }} />
                                    )}
                                    {job.last_run_at && (
                                        <Chip label={`Dernier : ${fmtDate(job.last_run_at)}`} size="small"
                                            sx={{ fontSize: 10, bgcolor: "#fafbfd", color: "#64748b" }} />
                                    )}
                                </Box>

                                {/* Last status */}
                                {lastStatus && statusStyle && (
                                    <Box display="flex" alignItems="center" gap={0.5} mb={1.5}
                                        sx={{ borderRadius: 1.5, px: 1, py: 0.5, bgcolor: statusStyle.bgcolor, width: "fit-content" }}>
                                        {statusStyle.icon}
                                        <Typography fontSize={11} fontWeight={600} color={statusStyle.color}>
                                            {lastStatus === "SUCCESS" ? "Succès" : "Échec"}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Footer */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={job.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(job)}
                                                sx={{
                                                    color: SECTION_COLORS.internalJobs.main,
                                                    bgcolor: `${SECTION_COLORS.internalJobs.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.internalJobs.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Exécuter maintenant">
                                            <span>
                                                <IconButton size="small" onClick={() => onRun(job)} disabled={!job.active}
                                                    sx={{ color: "#16a34a", bgcolor: "#f0fdf4", "&:hover": { bgcolor: "#dcfce7" }, width: 30, height: 30 }}>
                                                    <PlayArrowIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(job)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(job)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {filtered.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={filtered.length} page={page}
                    onPageChange={handlePageChange(tabIndex)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default InternalJobsTab;
